import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu as MenuIcon,
  Bell as BellIcon,
  User as UserIcon,
  Settings as SettingsIcon,
  LogOut as LogOutIcon,
  ChevronDown as ChevronDownIcon
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import NotificationCenter from '../NotificationCenter';
import { useDashboardStore } from '../../stores/dashboardStore';
import { MapPin } from 'lucide-react';
import { ViewablePlant } from '../../types/auth';


interface NavbarProps {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar, isSidebarOpen }) => {
  const {
    user,
    logout,
    accessContext,
    accessContextStatus,
    loadAccessContext,
  } = useAuthStore();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPlantMenuOpen, setIsPlantMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const plantMenuRef = useRef<HTMLDivElement>(null);

  const { alvId, setAlvIdFilter } = useDashboardStore();
  const [availablePlants, setAvailablePlants] = useState<Array<{value: string, label: string, can_edit: boolean}>>([]);

  // The access context is the source of truth for the plant selector.  It is
  // intentionally independent of workshop data so an empty dashboard does not
  // make a permitted plant disappear.
  useEffect(() => {
    if (user && accessContextStatus === 'idle') {
      void loadAccessContext();
    }
  }, [user, accessContextStatus, loadAccessContext]);

  useEffect(() => {
    const plants = (accessContext?.accessible_plants || [])
      .map((plant: ViewablePlant) => ({
        value: plant.alv_id || plant.code || plant.name,
        label: `${plant.alv_id || plant.code || plant.name}${plant.can_edit === false ? ' (view only)' : ''}`,
        can_edit: plant.can_edit !== false,
      }))
      .filter((plant, index, all) => plant.value && all.findIndex(item => item.value === plant.value) === index);
    setAvailablePlants([{ value: '', label: 'All Plants', can_edit: false }, ...plants]);

    // A stale plant selection must never be used to request a plant outside
    // the user's current context (for example after an administrator changes
    // access while the user is logged in).
    if (alvId && !plants.some(plant => plant.value === alvId)) {
      setAlvIdFilter(undefined);
      return;
    }

    // Normal users start on their resolved primary plant.  If the account is
    // unresolved, use the first explicitly assigned plant; an account with no
    // access remains on the intentional empty/all-authorized state.
    if (!alvId && accessContext && !accessContext.is_admin && plants.length > 0) {
      const primary = accessContext.primary_plant.resolved
        ? accessContext.primary_plant.plant
        : null;
      const defaultPlant = primary || accessContext.accessible_plants[0];
      const defaultValue = defaultPlant?.alv_id || defaultPlant?.code || defaultPlant?.name;
      if (defaultValue) {
        setAlvIdFilter(defaultValue);
      }
    }
  }, [accessContext, alvId, setAlvIdFilter]);


  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (plantMenuRef.current && !plantMenuRef.current.contains(event.target as Node)) {
        setIsPlantMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    return user.full_name || user.username || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <nav className="bg-primary shadow-sm border-b border-primary-600 fixed top-0 left-0 right-0 z-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and Menu Toggle */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 rounded-md text-white hover:text-gray-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-white lg:hidden"
              aria-label="Toggle sidebar"
            >
              <MenuIcon className="h-6 w-6" />
            </button>

            {/* Desktop menu button */}
            <button
              onClick={onToggleSidebar}
              className="hidden lg:block p-2 rounded-md text-white hover:text-gray-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Toggle sidebar"
            >
              <MenuIcon className="h-5 w-5" />
            </button>

            {/* Logos */}
            <div className="flex items-center space-x-4">
              {/* Autoliv Logo */}
              <img 
                src="/logos/autoliv_white_logo.png" 
                alt="Autoliv" 
                className="h-8 w-auto"
              />
              
              {/* Separator */}
              <div className="h-6 w-px bg-white/30 hidden sm:block"></div>
              
              {/* APS House Logo */}
              <img 
                src="/logos/APS House Plain (1).png" 
                alt="APS House" 
                className="h-8 w-auto hidden sm:block"
              />
            </div>

            {/* Title */}
            <div className="hidden md:block">
              <h1 className="text-xl font-semibold text-white">KATA Lean 4.0</h1>
            </div>
          </div>

          {/* Right side - Notifications and User Menu */}
          <div className="flex items-center space-x-4">

            {/* Global Plant Filter */}
            <div className="hidden md:block relative" ref={plantMenuRef}>
              <button
                onClick={() => setIsPlantMenuOpen(!isPlantMenuOpen)}
                disabled={accessContextStatus !== 'ready'}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70 transition-colors rounded-lg px-3 h-9 text-white text-sm font-medium focus:outline-none"
              >
                <MapPin className="h-4 w-4 text-white/80" />
                <span>
                  {accessContextStatus === 'loading'
                    ? 'Loading plants…'
                    : accessContextStatus === 'error'
                      ? 'Plants unavailable'
                      : alvId || 'All Plants'}
                </span>
                <ChevronDownIcon className="h-3.5 w-3.5 text-white/70" />
              </button>

              {isPlantMenuOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-64 overflow-y-auto">
                  {availablePlants.map((plant) => (
                    <button
                      key={plant.value}
                      onClick={() => {
                        setAlvIdFilter(plant.value || undefined);
                        setIsPlantMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        (alvId || '') === plant.value
                          ? 'text-primary font-medium bg-primary/5'
                          : 'text-gray-700'
                      }`}
                    >
                      {plant.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notifications */}

            <NotificationCenter />

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-md text-white hover:text-gray-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="User menu"
              >
                {/* User Avatar */}
                <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center text-primary text-sm font-medium">
                  {getUserInitials()}
                </div>
                
                {/* User Name (hidden on mobile) */}
                <span className="hidden sm:block text-sm font-medium text-white">
                  {getUserDisplayName()}
                </span>
                
                {/* Dropdown Arrow */}
                <ChevronDownIcon className="h-4 w-4 text-white/70" />
              </button>

              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    {user?.role_display && (
                      <p className="text-xs text-primary-600 mt-1">{user.role_display}</p>
                    )}
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <UserIcon className="h-4 w-4 mr-3 text-gray-500" />
                      Profile
                    </Link>
                    
                    <Link
                      to="/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <SettingsIcon className="h-4 w-4 mr-3 text-gray-500" />
                      Settings
                    </Link>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-200 py-1">
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOutIcon className="h-4 w-4 mr-3 text-gray-500" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
